import { computed, ref, watch } from 'vue';

import { createNpi, hasValidCheckDigit, npiValue } from '../../domain/model/Npi';
import { toProvider, toProviderPatch } from '../../infrastructure/mappers/providerMapper';

/**
 * The editing state behind the billing identity panel, and the seam to the stored shape.
 *
 * IT EXISTS FOR THE LAYER RULE, not for the state. A component may not reach `infrastructure/` — a
 * composable is where that translation is allowed to happen, which is what every other component in
 * this context relies on. The draft below is genuinely small, and the rule is what decides that it
 * lives here rather than in the template's own script.
 *
 * IT HOLDS NO SESSION, and that is the deliberate difference from `useClientCoverages` next door.
 * That one exists because its host's tab panels unmount on switch and staged edits must outlive
 * that. The staff dialog keeps its panels alive, and there is no collection to stage: one value
 * rides the host's own draft and the host's own save writes it. So there is nothing to flush, no
 * unit of work, and no repository.
 *
 * WHAT THE FIELD HOLDS IS NOT WHAT THE DOMAIN ACCEPTS, which is the reason for a separate ref rather
 * than reading the value straight off the record. Somebody typing the sixth digit of ten has a value
 * no NPI can be built from, and re-deriving the field from the domain each keystroke would take a
 * half-entered number away under their cursor.
 *
 * @param {import('vue').Ref<Object|null>} person - the stored person record the host is editing
 */
// eslint-disable-next-line import/prefer-default-export
export function useProviderIdentity(person) {
  const storedNpi = () => npiValue(toProvider(person.value)?.npi) ?? '';

  const draft = ref(storedNpi());

  // Keyed on the RECORD, never on the value. The host merges each patch back into the draft it holds
  // and passes it down again, so watching the value would return every keystroke and fight the
  // person typing.
  watch(() => person.value?.id, () => { draft.value = storedNpi(); });

  const entered = computed(() => (draft.value ?? '').trim());
  const isEmpty = computed(() => entered.value.length === 0);

  /** Present, and not the shape of an NPI at all. */
  const isRefused = computed(() => !isEmpty.value && createNpi(entered.value) === null);

  /** Accepted, and almost certainly mistyped. */
  const isSuspect = computed(() => {
    return !isEmpty.value && !isRefused.value && !hasValidCheckDigit(entered.value);
  });

  /**
   * The patch for what is in the field, or null when there is nothing to hand over.
   *
   * A refused value yields null rather than a patch carrying the raw string: it is not an NPI, so
   * emitting it would put a value the domain refuses into the document the host's save writes.
   *
   * @returns {{npi: string|null}|null}
   */
  function currentPatch() {
    if (isRefused.value) return null;

    return toProviderPatch(isEmpty.value ? null : createNpi(entered.value));
  }

  return {
    draft, isRefused, isSuspect, currentPatch,
  };
}
